import {
  TranscriptChange,
  TranscriptElement,
  TranscriptTextElement,
} from '../../types.ts/transcript';
import {
  getClosestNotRemovedTextIndexToRight,
  getClosestNotRemovedTextIndexToLeft,
  getClosestElementIndexToLeftByFilter,
} from '../utils';
import ChangeHandler from './ChangeHandler';

class RemoveHeadlessChangeHandler extends ChangeHandler {
  apply(
    transcriptionElementsMutable: TranscriptElement[],
    change: TranscriptChange & { type: 'remove_headless' },
  ) {
    this.reapplyKaraokeBreaksForChange(transcriptionElementsMutable, change);
    if (typeof change.index === 'number') {
      this.applyToSegment(
        transcriptionElementsMutable,
        change as TranscriptChange & { type: 'remove_headless'; index: number },
      );
      this.fixCapitalizationAt(transcriptionElementsMutable, change.index);
    } else {
      this.applyToIndividualElements(
        transcriptionElementsMutable,
        change as TranscriptChange & {
          type: 'remove_headless';
          index: number[];
        },
      );
      this.fixCapitalizationAt(transcriptionElementsMutable, change.index[0]);
    }
  }

  private applyToSegment(
    transcriptionElementsMutable: TranscriptElement[],
    change: TranscriptChange & { type: 'remove_headless'; index: number },
  ) {
    const elements = transcriptionElementsMutable;
    const firstWordIndex = getClosestNotRemovedTextIndexToRight(
      change.index,
      elements,
    );
    const firstWord = elements[firstWordIndex] as TranscriptTextElement;
    const lastWordIndex = getClosestNotRemovedTextIndexToLeft(
      change.index + change.count - 1,
      elements,
    );
    const lastWord = elements[lastWordIndex] as TranscriptTextElement;

    const startTs = firstWord!.ts;
    const endTs = lastWord!.end_ts;

    for (let i = change.index; i < change.index + change.count; i++) {
      if (i >= 0 && i < elements.length) {
        elements[i].state =
          change.type === 'remove_headless' ? 'removed' : change.type;
        delete elements[i].karaoke_break;
        delete elements[i].karaoke_break_end_ts_diff;
        delete elements[i].karaoke_break_start_ts_diff;
      }
    }

    // Copied from RemoveChangeHandler.ts but not sure if it is needed here
    if (
      startTs - (change.timeBufferBefore ?? 0) >=
      endTs + (change.timeBufferAfter ?? 0)
    ) {
      console.warn('Not deleted any text: Incorrect time range', change);
      return;
    }

    if (change.inPlace) {
      // do not shift everything after the cut
      return;
    }

    const timeChange =
      endTs -
      startTs +
      (change.timeBufferAfter ?? 0) +
      (change.timeBufferBefore ?? 0);

    for (let j = change.index + change.count; j < elements.length; j++) {
      const element = elements[j];
      if (element.type === 'text') {
        element.ts -= timeChange;
        element.end_ts -= timeChange;
      }
    }
  }

  private applyToIndividualElements(
    transcriptionElementsMutable: TranscriptElement[],
    change: TranscriptChange & { type: 'remove_headless'; index: number[] },
  ) {
    const elements = transcriptionElementsMutable;
    for (let idx of change.index) {
      if (idx >= 0 && idx < elements.length) {
        elements[idx].state =
          change.type === 'remove_headless' ? 'removed' : change.type;
        delete elements[idx].karaoke_break;
        delete elements[idx].karaoke_break_end_ts_diff;
        delete elements[idx].karaoke_break_start_ts_diff;
      }
    }
  }

  private reapplyKaraokeBreaksForChange(
    transcriptionElementsMutable: TranscriptElement[],
    change: TranscriptChange & { type: 'remove_headless' },
  ) {
    let done = false;
    const elements = transcriptionElementsMutable;
    const indexes =
      typeof change.index === 'number'
        ? Array.from(
            { length: change.count },
            (_, i) => (change.index as number) + i,
          )
        : change.index;
    for (let i of indexes) {
      const element = elements[i];
      if (!element.karaoke_break) continue;
      const prevElementIndex = getClosestElementIndexToLeftByFilter(
        indexes[0] - 1,
        elements,
        el =>
          Boolean(
            el.state !== 'cut' &&
              el.state !== 'removed' &&
              el.state !== 'muted' &&
              el.value,
          ),
      );
      const prevElement = elements[prevElementIndex];
      if (!prevElement) return;

      const prevTextElementIndex = getClosestElementIndexToLeftByFilter(
        prevElementIndex,
        elements,
        el =>
          Boolean(
            el.type === 'text' &&
              el.value &&
              el.state !== 'cut' &&
              el.state !== 'removed' &&
              el.state !== 'muted',
          ),
      );
      const prevKaraokeBreakIndex = getClosestElementIndexToLeftByFilter(
        prevElementIndex,
        elements,
        el => Boolean(el.karaoke_break),
      );
      if (
        elements[prevTextElementIndex] &&
        prevTextElementIndex > prevKaraokeBreakIndex
      ) {
        prevElement.karaoke_break = true;
      }
      return;
    }
  }
}

export default RemoveHeadlessChangeHandler;
